import React, {
    MouseEvent,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react'
import { Link } from 'react-router-dom'
import {
    Typography,
    Menu,
    MenuItem,
    ListItemIcon,
    Fade,
    IconButton,
    Divider,
} from '@mui/material'
import {
    EditIcon,
    DeleteIcon,
    VisibilityIcon,
    MoreVertIcon,
} from '../components'
import { translate } from '../i18n'
import { KeyPair } from '../model'
import { AppContext } from '../context'

interface KeyPairMenuProps {
    keyPair: KeyPair
    onDetails(keyPair: KeyPair): any
    onDelete(keyPair: KeyPair): any
}

export default function KeyPairMenu({
    keyPair,
    onDetails,
    onDelete,
}: KeyPairMenuProps) {
    const context = useContext(AppContext)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

    const isDeleteGranted = useMemo(
        () => context.isGranted('KeyPairsDelete'),
        [context]
    )

    const onClickedOptions = useCallback((event: MouseEvent<HTMLElement>) => {
        event.stopPropagation()
        setAnchorEl(event.currentTarget)
    }, [])

    const onClickedClose = useCallback(() => {
        setAnchorEl(null)
    }, [])

    return (
        <>
            <IconButton
                aria-label="options"
                color="default"
                size="small"
                onClick={onClickedOptions}
            >
                <MoreVertIcon />
            </IconButton>
            {anchorEl && (
                <Menu
                    id="key-pairs-item"
                    anchorEl={anchorEl}
                    keepMounted
                    onClose={onClickedClose}
                    open
                    TransitionComponent={Fade}
                >
                    {context.isGranted('KeyPairsUpdate') && (
                        <MenuItem
                            component={Link}
                            to={`/keypairs/${keyPair.id}/edit`}
                            onClick={onClickedClose}
                            dense
                        >
                            <ListItemIcon>
                                <EditIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <Typography variant="inherit">
                                {translate('buttons.edit')}
                            </Typography>
                        </MenuItem>
                    )}
                    {context.isGranted('KeyPairsRead') && (
                        <MenuItem onClick={() => onDetails(keyPair)} dense>
                            <ListItemIcon>
                                <VisibilityIcon
                                    fontSize="small"
                                    color="primary"
                                />
                            </ListItemIcon>
                            <Typography variant="inherit">
                                {translate('buttons.see')}
                            </Typography>
                        </MenuItem>
                    )}
                    {isDeleteGranted && <Divider />}
                    {isDeleteGranted && (
                        <MenuItem onClick={() => onDelete(keyPair)} dense>
                            <ListItemIcon>
                                <DeleteIcon fontSize="small" color="error" />
                            </ListItemIcon>
                            <Typography variant="inherit">
                                {translate('buttons.delete')}
                            </Typography>
                        </MenuItem>
                    )}
                </Menu>
            )}
        </>
    )
}
